import {
  Box,
  DeprecatedIcon,
  Flex,
  IconName,
  ReadMoreMarkdown,
  Text,
  Tooltip,
  Typography,
} from '@fullstory/components';
import { Dashboard } from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';
import { getDashboardPath } from '@fullstory/routes';
import React, { FC } from 'react';
import { LIBRARY_OBJECT_TOOLTIP_VARIANT_STYLES } from '../../constants';
import { LibraryObjectTooltip } from '../LibraryObjectTooltip';
import { TitleLink } from '../TitleLink';

export const DASHBOARD_OBJECT_TOOLTIP_ICON_NAME: IconName = 'dashboard';

export const DashboardTooltipContent: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
  if (!dashboard.description) return null;

  return (
    <Box data-component="DashboardTooltipContent">
      <Text appearance="bodySmall" tag="div">
        <ReadMoreMarkdown maxLines={2} content={dashboard.description} ignoreMultilineComponents />
      </Text>
    </Box>
  );
};

interface DashboardNameProps {
  className?: string;
  dashboard?: Dashboard;
  orgId: string;
  inverse?: boolean;
  preventLink?: boolean;
  name?: React.ReactNode;
  displaySettings?: boolean;
  variant: keyof typeof LIBRARY_OBJECT_TOOLTIP_VARIANT_STYLES;
  placement?: React.ComponentProps<typeof Tooltip>['placement'];
  ellipsize?: boolean;
}

export const DashboardName: FC<DashboardNameProps> = ({
  className,
  dashboard,
  orgId,
  inverse,
  name,
  preventLink,
  variant,
  placement = 'bottom-start',
  ellipsize = false,
}) => {
  const { typographyVariant, textColor, iconSize, iconMargin, lineHeight } =
    LIBRARY_OBJECT_TOOLTIP_VARIANT_STYLES[variant];
  const dashboardNameString = dashboard?.name ?? '';

  const displayIcon = iconSize && iconMargin;
  const dashboardName = (
    <Flex justifyContent={ellipsize ? 'flex-start' : 'center'}>
      {displayIcon && (
        <Box marginRight={iconMargin}>
          <DeprecatedIcon
            name="grammar/dashboards"
            color={inverse ? 'neutrals-n0' : 'neutrals-n7'}
            size={iconSize}
          />
        </Box>
      )}
      <Typography
        variant={typographyVariant}
        color={inverse ? 'neutrals-n0' : textColor}
        lineHeight={lineHeight ?? 1.8}
        ellipsize={ellipsize}
      >
        {name ?? dashboardNameString}
      </Typography>
    </Flex>
  );

  if (!dashboard) {
    return null;
  }

  const pathname = getDashboardPath(orgId, dashboard.id as int64);

  return (
    <Box
      data-component="DashboardName"
      display="flex"
      justifyContent="flex-start"
      className="fs-unmask"
      overflow="hidden"
    >
      <Box maxWidth={1}>
        <LibraryObjectTooltip
          name={dashboardNameString}
          metadata={dashboard.metadata}
          icon={DASHBOARD_OBJECT_TOOLTIP_ICON_NAME}
          placement={placement}
          retentionTooltip={null}
          content={<DashboardTooltipContent dashboard={dashboard} />}
          className={className}
        >
          <TitleLink to={!preventLink ? pathname : undefined}>{dashboardName}</TitleLink>
        </LibraryObjectTooltip>
      </Box>
    </Box>
  );
};
